import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import * as models from "liberty-core/api/models";

type AppointmentData = {
  appointmentTypeId: number;
  selectState: string;
  radioState: string;
  appointmentDate: Date | undefined;
  appointmentTime: models.TimeString | undefined;
  appointmentTimeSlotId: number | undefined;
  questions: models.AppointmentQuestion[];
  // selectedAnswers: models.AppointmentAnswer[];
};

type AppointmentDataRaw = {
  appointmentTypeId: number;
  selectState: string;
  radioState: string;
  appointmentDate: string | undefined;
  appointmentTime: models.TimeString | undefined;
  appointmentTimeSlotId: number | undefined;
  questions: models.AppointmentQuestion[];
  // selectedAnswers: models.AppointmentAnswer[];
};

const emptyAppointmentData = {
  appointmentTypeId: -1,
  selectState: "",
  radioState: "",
  appointmentDate: undefined,
  appointmentTime: undefined,
  appointmentTimeSlotId: undefined,
  // selectedAnswers: [],
  questions: [],
};

const appointmentDataSessionKey = "appointmentData";

export const AppointmentContext = createContext<{
  appointmentData: AppointmentData;
  setAppointmentData: (newData: AppointmentData) => void;
  clearAppointmentData: () => void;
}>({
  appointmentData: emptyAppointmentData,
  setAppointmentData: () => null,
  clearAppointmentData: () => null,
});

export const AppointmentProvider = ({children}: {children: ReactNode}) => {
  const [appointmentData, setAppointmentData] =
    useState<AppointmentData>(emptyAppointmentData);

  const clearAppointmentData = useCallback(() => {
    setAppointmentData(emptyAppointmentData);
  }, []);

  return (
    <AppointmentContext.Provider
      value={{
        appointmentData,
        setAppointmentData,
        clearAppointmentData,
      }}
    >
      {children}
    </AppointmentContext.Provider>
  );
};

function getSessionData() {
  const returnedData = sessionStorage.getItem(appointmentDataSessionKey);
  const appointmentDataRaw =
    returnedData !== null
      ? (JSON.parse(returnedData) as AppointmentDataRaw)
      : emptyAppointmentData;
  return {
    ...appointmentDataRaw,
    appointmentDate: appointmentDataRaw.appointmentDate
      ? new Date(appointmentDataRaw.appointmentDate)
      : undefined,
  };
}

export function useAppointmentData() {
  const {
    appointmentData: appointmentDataContext,
    setAppointmentData: setAppointmentDataContext,
    clearAppointmentData: clearAppointmentDataContext,
  } = useContext(AppointmentContext);

  const appointmentDataSession =
    appointmentDataContext === emptyAppointmentData
      ? getSessionData()
      : undefined;

  const setAppointmentData = (newData: AppointmentData) => {
    setAppointmentDataContext(newData);
    sessionStorage.setItem(appointmentDataSessionKey, JSON.stringify(newData));
  };

  const clearAppointmentData = () => {
    clearAppointmentDataContext();
    sessionStorage.setItem(
      appointmentDataSessionKey,
      JSON.stringify(emptyAppointmentData),
    );
  };

  return {
    appointmentData: appointmentDataSession ?? appointmentDataContext,
    setAppointmentData,
    clearAppointmentData,
  };
}
